import {
  Button,
  ButtonGroup,
  Card,
  Grid,
  GridColumn,
  GridRow,
  Select,
  SelectOptionType,
  Table,
  TableColumns,
} from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../hooks/useColumnsSort';
import { SolverEngine, useSolverJobDetailsQuery, useSolverJobsQuery } from '../../types/planung-graphql-client-defs';
import {
  cancelTimetableVersionGeneration,
  importTimetableVersionGeneration,
  restartTimetableVersionMonitor,
  triggerSolverCleanup,
} from '../../utils/timetable/timetableVersion';
import { extractSolverLog, formatPossiblyJson } from './mathplanJobFormatters';

type SolverJobsListDataType = {
  jobId: string;
  engine: SolverEngine;
  status: string;
  schoolyear: string;
  createdAt?: dayjs.Dayjs;
  updatedAt?: dayjs.Dayjs;
  gap: number;
  solutionFound: boolean;
};

export const MathplanJobsTable = observer(() => {
  const { t } = useTranslation();

  const { sorting, saveSorting } = useColumnsSort('solver-job-list');

  const [selectedJob, setSelectedJob] = useState<SolverJobsListDataType | null>(null);
  const [engineFilter, setEngineFilter] = useState<'all' | SolverEngine>('all');

  // Nur noch die aktiven Engines: ASC/Untis (Offline-Solver) sind abgeschafft. Alte Jobs davon
  // bleiben in der Liste sichtbar (über "Alle") und behalten ihr Engine-Label.
  const engineFilterOptions: SelectOptionType[] = useMemo(
    () => [
      { value: 'all', label: t('solver.adminEngineAll') },
      { value: SolverEngine.Mathplan, label: t('solver.engine.mathplan.name') },
      { value: SolverEngine.Timefold, label: t('solver.engine.timefold.name') },
    ],
    [t],
  );

  const [{ data }, refetchJobs] = useSolverJobsQuery();

  // Aufgabe, Lösung und Fehler nur für den angeklickten Lauf.
  const [{ data: detailsData }] = useSolverJobDetailsQuery({
    variables: { jobId: selectedJob?.jobId ?? '' },
    pause: !selectedJob,
    requestPolicy: 'cache-and-network',
  });
  const details = detailsData?.solverJobDetails?.jobId === selectedJob?.jobId ? detailsData?.solverJobDetails : null;
  const jobProblem = details?.problem ?? undefined;
  const jobSolution = details?.solution ?? undefined;
  const jobErrors = details?.errors ?? undefined;

  const solverJobsRows: SolverJobsListDataType[] = useMemo(() => {
    return (
      data?.solverJobs.map((o) => {
        return {
          ...o,
          schoolyear: o.schoolYearName + ' / ' + o.timetableName + '#' + o.version,
          createdAt: dayjs(o.createdAt),
          updatedAt: dayjs(o.updatedAt),
        };
      }) ?? []
    );
  }, [data]);

  const filteredJobs = useMemo(() => {
    if (engineFilter === 'all') {
      return solverJobsRows;
    }
    return solverJobsRows.filter((r) => r.engine === engineFilter);
  }, [solverJobsRows, engineFilter]);

  const tableColumns: TableColumns<SolverJobsListDataType>[] = useMemo(() => {
    return [
      {
        header: t('common.createdAt'),
        accessorKey: 'createdAt',
        id: 'createdAt',
        type: 'date',
        accessorFn: (row) => {
          return row.createdAt?.format('DD.MM.YY HH:mm:ss') ?? '';
        },
        size: 150,
      },
      {
        header: t('solver.adminEngine'),
        accessorKey: 'engine',
        id: 'engine',
        canExpand: true,
      },
      {
        header: t('common.name'),
        accessorKey: 'schoolyear',
        id: 'schoolyear',
        canExpand: true,
      },
      {
        header: t('common.status'),
        accessorKey: 'status',
        id: 'status',
        canExpand: true,
      },
      {
        header: t('mathplan.solutionFound'),
        accessorKey: 'solutionFound',
        id: 'solutionFound',
        type: 'boolean',
      },
    ];
  }, [t]);

  const copyToClipboard = async (value?: string) => {
    if (!value) {
      return;
    }
    await navigator.clipboard.writeText(value);
  };

  return (
    <Grid>
      <GridRow>
        <GridColumn width={12}>
          <GridRow spacingBottom='m'>
            <GridColumn width={4}>
              <div className='mb-2'>{t('solver.adminEngine')}</div>
              <Select
                dense
                name='solver-admin-engine-filter'
                options={engineFilterOptions}
                value={engineFilterOptions.find((o) => o.value === engineFilter)}
                onChange={(event) => {
                  const o = event as SelectOptionType | null;
                  const v = o?.value;
                  if (v === 'all') {
                    setEngineFilter('all');
                  } else if (v === SolverEngine.Mathplan || v === SolverEngine.Timefold) {
                    setEngineFilter(v);
                  }
                }}
                menuPosition='fixed'
              />
            </GridColumn>
            <GridColumn width={4}>
              <div className='mb-2'>&nbsp;</div>
              <Button
                onClick={async () => {
                  await triggerSolverCleanup();
                  refetchJobs({ requestPolicy: 'network-only' });
                }}
              >
                Cleanup-Job triggern
              </Button>
            </GridColumn>
          </GridRow>
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn width={6} fitRowHeight>
          <Table<SolverJobsListDataType>
            showBorderRadius
            showShadow
            canScroll
            minHeight={600}
            isDesktop={true}
            isOnWhite={false}
            onSortingChange={saveSorting}
            sorting={sorting}
            columns={tableColumns}
            data={filteredJobs}
            showActionBar
            actionBarSettings={{
              showExpertFilter: false,
              showAddButton: false,
              showBulkEdit: false,
              showPrintButton: false,
            }}
            lastColWidth='120px'
            lastCol={(row) => {
              return (
                <ButtonGroup>
                  <Button
                    onClick={() => {
                      setSelectedJob(row.original);
                    }}
                  >
                    Details
                  </Button>
                </ButtonGroup>
              );
            }}
          />
        </GridColumn>
        {selectedJob && (
          <GridColumn width={6} fitRowHeight>
            <GridRow headline={'Stundenplan'}>
              <GridColumn width={12}>
                <p>Solver-ID: {selectedJob?.jobId}</p>
                <p>Engine: {selectedJob?.engine}</p>
                <p>Name / Schuljahr: {selectedJob?.schoolyear}</p>
                <p>Erstellt: {selectedJob?.createdAt?.toISOString()}</p>
                <p>zuletzt aktualisiert: {JSON.stringify(selectedJob?.updatedAt)}</p>
                <p>gap: {selectedJob?.gap}</p>
              </GridColumn>
            </GridRow>
            {!['FINISHED', 'ERROR', 'CANCELLED'].includes(selectedJob.status) && (
              <Card canScroll contentPadding={'s'} fitContent>
                <Button
                  onClick={async () => {
                    await cancelTimetableVersionGeneration(selectedJob.jobId);
                    refetchJobs({ requestPolicy: 'network-only' });
                  }}
                >
                  Abbrechen
                </Button>
              </Card>
            )}
            <Card canScroll contentPadding={'s'} fitContent>
              <Button
                onClick={async () => {
                  await restartTimetableVersionMonitor(selectedJob.jobId);
                  refetchJobs({ requestPolicy: 'network-only' });
                }}
              >
                Monitor neu starten
              </Button>
            </Card>
            {['FINISHED', 'IMPORTED', 'SOLUTION', 'SOLVED'].includes(selectedJob.status) && (
              <Card canScroll contentPadding={'s'} fitContent>
                <Button
                  onClick={async () => {
                    await importTimetableVersionGeneration(selectedJob.jobId);
                    refetchJobs({ requestPolicy: 'network-only' });
                  }}
                >
                  Erneut importieren
                </Button>
              </Card>
            )}
            {jobProblem && (
              <Card
                header={{
                  headline: 'Problem (XML)',
                  actions: [
                    {
                      hierarchy: 'secondary',
                      text: 'Kopieren',
                      callback: () => {
                        void copyToClipboard(jobProblem);
                      },
                    },
                    {
                      hierarchy: 'secondary',
                      text: 'Download',
                      callback: () => {
                        const file = new Blob([jobProblem ?? ''], { type: 'text/xml' });
                        const url = URL.createObjectURL(file);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `problem-${selectedJob?.jobId}.xml`;
                        document.body.appendChild(a);
                        a.click();
                      },
                    },
                  ],
                }}
                collapsibleOn={'always'}
                canScroll
                contentPadding={'s'}
                fitContent
                isCollapsed
              >
                <pre>{jobProblem}</pre>
              </Card>
            )}
            {jobSolution && (
              <Card
                header={{
                  headline: 'Solution (JSON)',
                  actions: [
                    {
                      hierarchy: 'secondary',
                      text: 'Kopieren',
                      callback: () => {
                        void copyToClipboard(jobSolution);
                      },
                    },
                  ],
                }}
                collapsibleOn={'always'}
                canScroll
                contentPadding={'s'}
                fitContent
                isCollapsed
              >
                <pre>{formatPossiblyJson(jobSolution)}</pre>
              </Card>
            )}
            {jobSolution && (
              <Card header={{ headline: 'Solution (LOG)' }} canScroll contentPadding={'s'} fitContent>
                <pre>{extractSolverLog(jobSolution)}</pre>
              </Card>
            )}
            {jobErrors && (
              <Card
                header={{
                  headline: 'Errors',
                  actions: [
                    {
                      hierarchy: 'secondary',
                      text: 'Kopieren',
                      callback: () => {
                        void copyToClipboard(jobErrors);
                      },
                    },
                  ],
                }}
                collapsibleOn={'always'}
                canScroll
                contentPadding={'s'}
                fitContent
                isCollapsed
              >
                <pre>{formatPossiblyJson(jobErrors)}</pre>
              </Card>
            )}
          </GridColumn>
        )}
      </GridRow>
    </Grid>
  );
});
